/* eslint-disable no-console */
/* eslint-disable prefer-spread */
import moment from 'moment';
import { CustomIntervalEntity } from '../../apis/custom-interval/custom-interval-entity';
import { ScheduleRowEntity } from '../../apis/schedule/schedule-row-entity';
import { SerieSettingEntity } from '../../apis/serie/serie-setting-entity';
import { GameRow } from './game-row';
import { TeamGame, TeamDropInfo, TeamDropInfoType } from './team-game';
import { OpponentsViewModel } from './opponents-view-model';
import { EventAggregator } from '@aurelia/kernel';
import { ScheduleUpdatedMessage } from '../../messages/schedule-updated-message';
import { RemovedTeam } from './removed-team';
import { TeamSettingEntity } from '../../apis/team/team-setting-entity';
import { VenueEntity } from '../../apis/venue/venue-entity';
import { VenueModel } from './venue-model';
import { ScheduleFieldEntity } from '../../apis/schedule/schedule-field-entity';
export class ScheduleViewModel {

    public static freeSlotTeamName: string = '';
    public id: string = null;
    private _opponentList: OpponentsViewModel[] = [];
    public get opponentList(): OpponentsViewModel[] {
        return this._opponentList;
    }
    public set opponentList(value: OpponentsViewModel[]) {
        this._opponentList = value;
    }
    public removedTeams: RemovedTeam[] = [];
    public intervalCount: number = 0;
    public gameRows: GameRow[] = [];
    public readonly: boolean = true;
    public venueModels: VenueModel[] = [];
    private _scheduleRows: ScheduleRowEntity[] = [];
    private _extraGames: TeamExtraGame[] = [];
    private _serie: SerieSettingEntity = null;
    private _customIntervals: CustomIntervalEntity[] = [];
    private _teamSettings: TeamSettingEntity[] = [];
    private _eventAggregator: EventAggregator = null;
    constructor(eventAggregator: EventAggregator) {
        this._eventAggregator = eventAggregator;
    }

    public get fields(): number {
        return this.venueModels.length;
    }
    public initialize(games: ScheduleRowEntity[], serie: SerieSettingEntity, customIntervals: CustomIntervalEntity[], teamSettings: TeamSettingEntity[], venues: VenueEntity[], fields: ScheduleFieldEntity[], readonly: boolean): void {
        this.readonly = readonly;
        this._serie = serie;
        if (!games || !serie || !customIntervals) {
            return;
        }

        this._customIntervals = customIntervals;
        this._teamSettings = teamSettings;
        this._scheduleRows.clear();
        this._scheduleRows.addRange(games);
        this.intervalCount = Math.max.apply(Math, games.map(g => g.interval + 1));
        // Build venue models from the field-to-venue mapping table
        const allFieldIds = games.select(g => g.fieldId).distinct<number>().orderBy(f => f);
        this.venueModels = allFieldIds.select(fieldId => {
            const fieldEntry = fields.find(f => f.fieldNumber === fieldId);
            if (fieldEntry?.venueId != null) {
                const venue = venues.firstOrDefault(v => v.id === fieldEntry.venueId);
                return new VenueModel(fieldId, venue || null, fieldEntry.venueId);
            }
            return new VenueModel(fieldId, null);
        });
        this._setup();
    }

    public addRow(): void {
        for (const venue of this.venueModels) {
            const game = new ScheduleRowEntity();
            game.fieldId = venue.fieldId;
            game.homeTeam = ScheduleViewModel.freeSlotTeamName;
            game.interval = this.intervalCount;
            game.opponentTeam = ScheduleViewModel.freeSlotTeamName;
            this._scheduleRows.add(game);
        }
        this.intervalCount++;
        this._setup();
    }

    public deleteLastRow(): void {

        const gamesToRemove = this._scheduleRows.where(g => g.interval === (this.intervalCount - 1));
        gamesToRemove.forEach(g => this._scheduleRows.remove(g));
        this.intervalCount--;
        this._setup();
    }
    public addExtraGame(team: TeamSettingEntity): void {
        let extraGame = this._extraGames.firstOrDefault(e => e.teamSetting === team);
        if (extraGame == null) {
            extraGame = new TeamExtraGame();
            extraGame.teamSetting = team;
            this._extraGames.add(extraGame);
        }
        extraGame.extraGameCount += 1;
    }

    public getEditedScheduleRows(): ScheduleRowEntity[] {
        return this.gameRows.selectMany(gr => gr.games).select(g => g.game).where(g => !g.isEmpty);
    }
    public removeExtraGame(team: TeamSettingEntity): void {
        const extraGame = this._extraGames.firstOrDefault(e => e.teamSetting === team);
        if (extraGame != null) {
            this._extraGames.remove(extraGame);
        }
    }

    public dropRemoveTeam(ev: DragEvent): void {

        ev.preventDefault();
        const sourceTeam = ev.dataTransfer.getData('team');
        const sourceTeamType = parseInt(ev.dataTransfer.getData('type'), 10) as TeamDropInfoType;
        const sourceInterval = parseInt(ev.dataTransfer.getData('interval'), 10);
        const sourceVenueKey = ev.dataTransfer.getData('venueKey');
        const dropData = new TeamDropInfo(sourceTeam, sourceTeamType, sourceInterval, sourceVenueKey, ScheduleViewModel.freeSlotTeamName, TeamDropInfoType.removedTeam, -1, '');
        // console.log('dropdata remove team: ' + dropData.toString());
        this._onDropCallback(dropData);

    }

    public allow(ev: DragEvent): void {
        ev.preventDefault();
    }

    private _setup(): void {

        this.gameRows.clear();
        const customIntervals = this._customIntervals.slice();
        let startTime = this._serie.startTime.toMoment();
        // Custom rows before first game
        const customRowsBeforeFirstGame = customIntervals.where(ci => ci.afterInterval === -1);
        for (const customInterval of customRowsBeforeFirstGame) {
            const customRow = new GameRow(-1, startTime.subtract(customInterval.duration, 'minutes'), customInterval.duration, this.readonly);
            customRow.message = customInterval.text;
            startTime.add(customInterval.duration, 'minutes');
            this.gameRows.add(customRow);
            customIntervals.remove(customInterval);
        }

        const matchLengthInMinutes = this._serie.periods * this._serie.periodLength + this._serie.periodPause * (this._serie.periods - 1);
        const intervalCount = this._scheduleRows.orderBy(g => g.interval).lastOrDefault().interval + 1;
        for (let currentInterval = 0; currentInterval < intervalCount; currentInterval++) {
            const rows = this._scheduleRows.where(s => s.interval === currentInterval);


            const gameRow = new GameRow(currentInterval, startTime, matchLengthInMinutes, this.readonly);
            this.gameRows.add(gameRow);

            for (const venueModel of this.venueModels) {
                let game: ScheduleRowEntity = null;
                game = rows != null ? rows.firstOrDefault(g => g.fieldId === venueModel.fieldId) : null;

                if (game == null) {
                    game = new ScheduleRowEntity();
                    game.fieldId = venueModel.fieldId;
                    game.interval = currentInterval;
                    game.homeTeam = ScheduleViewModel.freeSlotTeamName;
                    game.opponentTeam = ScheduleViewModel.freeSlotTeamName;
                    // this.scheduleRows.add(game);
                }

                startTime = moment(gameRow.endTime);
                const teamGame = new TeamGame(game, this.fields, (dropData: TeamDropInfo) => this._onDropCallback(dropData), this._teamSettings);
                gameRow.addGame(teamGame);
            }
            let addedCustomInterval = false;
            for (const customInterval of customIntervals) {
                if (customInterval.afterInterval === currentInterval) {
                    const customRow = new GameRow(currentInterval, startTime, customInterval.duration, this.readonly);
                    customRow.message = customInterval.text;
                    startTime.add(customInterval.duration, 'minutes');
                    this.gameRows.add(customRow);
                    addedCustomInterval = true;
                    customIntervals.remove(customInterval);
                }
            }
            if (!addedCustomInterval) {
                startTime.add(this._serie.gamePause, 'minutes');
            }
        }

        const lastRow = this.gameRows.where(g => !g.isCustomRow).lastOrDefault();
        const customRowsAfterLastGame = customIntervals.where(ci => ci.afterInterval >= lastRow.interval).orderBy(ci => ci.afterInterval);
        startTime = moment(lastRow.endTime);
        let nextInterval = lastRow.interval + 1;
        for (const customInterval of customRowsAfterLastGame) {
            const customRow = new GameRow(nextInterval, startTime, customInterval.duration, this.readonly);
            customRow.message = customInterval.text;
            this.gameRows.add(customRow);
            startTime.add(customInterval.duration, 'minutes');
            nextInterval++;
            customIntervals.remove(customInterval);
        }

        this.opponentList.clear();

        for (const game of this._scheduleRows) {
            if (game.homeTeam !== ScheduleViewModel.freeSlotTeamName) {
                let homeTeamList = this.opponentList.firstOrDefault(ol => ol.team === game.homeTeam);
                if (!homeTeamList) {
                    homeTeamList = new OpponentsViewModel(game.homeTeam);
                    this.opponentList.add(homeTeamList);
                }
                homeTeamList.addOpponent(game.opponentTeam);
            }
            if (game.opponentTeam !== ScheduleViewModel.freeSlotTeamName) {
                let opponentTeamList = this.opponentList.firstOrDefault(ol => ol.team === game.opponentTeam);
                if (!opponentTeamList) {
                    opponentTeamList = new OpponentsViewModel(game.opponentTeam);
                    this.opponentList.add(opponentTeamList);
                }
                opponentTeamList.addOpponent(game.homeTeam);
            }
        }

        this._addRemovedTeams();
    }

    private _addRemovedTeams(): void {
        this.removedTeams.clear();
        if (!this._teamSettings) {
            return;
        }

        for (const team of this._teamSettings) {
            const extraGameSetting = this._extraGames.firstOrDefault(e => e.teamSetting === team);
            const extraGameCount = extraGameSetting != null ? extraGameSetting.extraGameCount : 0;
            const scheduleRows = this.gameRows.selectMany(tg => tg.games).where(s => s.homeTeam === team.name || s.opponentTeam === team.name);
            const gamesLeft = team.getEffectiveGameCount(this._serie.gameCount) + extraGameCount - scheduleRows.length;
            if (gamesLeft > 0) {
                this.removedTeams.add(new RemovedTeam(team.name, gamesLeft, null, 'transparent', (dropDataParam: TeamDropInfo) => this._onDropCallback(dropDataParam)));
            }
            if (gamesLeft < 0) {
                this.addExtraGame(team);
            }
        }
    }

    private _onDropCallback(dropData: TeamDropInfo): void {
    // console.log('dropdata: ' + dropData.toString());

        // Handle source team
        let sourceGame: TeamGame = null;
        let targetGame: TeamGame = null;
        if (dropData.sourceTeamType === TeamDropInfoType.homeTeam) {
            sourceGame = this.gameRows.selectMany(gr => gr.games).firstOrDefault(s => dropData.sourceVenueKey === s.venueKey && dropData.sourceInterval === s.interval && dropData.sourceTeam === s.homeTeam);
            // sourceGame = this.scheduleRows.firstOrDefault(s => dropData.sourceVenueKey === s.venueKey && dropData.sourceInterval === s.interval && dropData.sourceTeam === s.homeTeam);
            sourceGame.homeTeam = dropData.targetTeam;
        } else if (dropData.sourceTeamType === TeamDropInfoType.opponentTeam) {
            sourceGame = this.gameRows.selectMany(gr => gr.games).firstOrDefault(s => dropData.sourceVenueKey === s.venueKey && dropData.sourceInterval === s.interval && dropData.sourceTeam === s.opponentTeam);
            // sourceGame = this.scheduleRows.firstOrDefault(s => dropData.sourceVenueKey === s.venueKey && dropData.sourceInterval === s.interval && dropData.sourceTeam === s.opponentTeam);
            sourceGame.opponentTeam = dropData.targetTeam;
        } else if (dropData.sourceTeamType === TeamDropInfoType.removedTeam) {
            // const removedTeam = this.removedTeams.firstOrDefault(s => dropData.sourceTeam === s.teamName);
            // if (dropData.targetTeam === ScheduleViewModel.freeSlotTeamName) {
            //   this.removedTeams.remove(removedTeam);
            // } else {
            //   removedTeam.teamName = dropData.targetTeam;
            // }
        }

        // Handle target team
        if (dropData.targetTeamType === TeamDropInfoType.homeTeam) {
            const gamesInThisInterval = this.gameRows.selectMany(gr => gr.games).where(s => dropData.targetInterval === s.interval);
            targetGame = gamesInThisInterval.firstOrDefault(s => dropData.targetVenueKey === s.venueKey && dropData.targetTeam === s.homeTeam);
            console.log(`Handle target team: ${dropData.sourceTeam}`);
            if (targetGame.fieldId == null) {
                targetGame.fieldId = this._findFirstFreeField(gamesInThisInterval, targetGame);
                console.log(`Updating fieldId to: ${targetGame.fieldId}`);
            } else {
                console.log(`Keeping fieldId : ${targetGame.fieldId}`);
            }
            targetGame.homeTeam = dropData.sourceTeam;

        } else if (dropData.targetTeamType === TeamDropInfoType.opponentTeam) {
            const gamesInThisInterval = this.gameRows.selectMany(gr => gr.games).where(s => dropData.targetInterval === s.interval);
            targetGame = gamesInThisInterval.firstOrDefault(s => dropData.targetVenueKey === s.venueKey && dropData.targetInterval === s.interval && dropData.targetTeam === s.opponentTeam);
            console.log(`Handle target team: ${dropData.sourceTeam}`);
            if (targetGame.fieldId == null) {
                targetGame.fieldId = this._findFirstFreeField(gamesInThisInterval, targetGame);
                console.log(`Updating fieldId to: ${targetGame.fieldId}`);
            } else {
                console.log(`Keeping fieldId : ${targetGame.fieldId}`);
            }
            targetGame.opponentTeam = dropData.sourceTeam;

        } else if (dropData.targetTeamType === TeamDropInfoType.removedTeam) {
            // const removedTeam = new RemovedTeam(dropData.sourceTeam, (dropDataParam: TeamDropInfo) => this._onDropCallback(dropDataParam));
            // this.removedTeams.add(removedTeam);
        }
        // this._setup();
        this._scheduleRows.clear();
        this._scheduleRows.addRange(this.getEditedScheduleRows());
        this._eventAggregator.publish(new ScheduleUpdatedMessage());
    }

    private _findFirstFreeField(gamesInThisInterval: TeamGame[], targetGame: TeamGame): number {
        if (targetGame.isEmpty && targetGame.fieldId != null) {
            return targetGame.fieldId; // Keep fieldId
        }
        const gamesWithFieldId = gamesInThisInterval.where(g => g.fieldId != null).orderBy(g => g.fieldId);
        for (let i = 0; i < gamesWithFieldId.length; i++) {
            if (gamesWithFieldId[i].fieldId !== i + 1) { // Found unoccupied field, use it
                return i + 1;
            }
        }
        return gamesWithFieldId.length + 1; // Add field to be sure not to use same fieldId as another team if venue is unset.
    }

}
export class TeamExtraGame {
    public teamSetting: TeamSettingEntity = null;
    public extraGameCount: number = 0;
}


